import { useEffect, useReducer } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ColumnFilterValue,
  FilterParam,
  isLookup,
  ListFilters,
  MenuOptionFilterValue,
  StoredColumnFilterValue,
} from "../types";

export interface UseListFiltersReturn {
  state: ListFilters;
  nextPage: () => void;
  prevPage: () => void;
  setSearchTerm: (term: string) => void;
  setOrder: (orderBy: string, desc: boolean) => void;
  setFilters: (fieldName: string, value: ColumnFilterValue) => void;
  getFilters: (fieldName: string) => StoredColumnFilterValue;
  isSearchable: boolean;
}

interface UseListFiltersOptions {
  isSearchable: boolean;
  filters?: FilterParam[];
  limit?: number;
  prefix?: string;
}

type Action =
  | { type: "SET_PAGE"; payload: number }
  | { type: "NEXT_PAGE" }
  | { type: "PREV_PAGE" }
  | { type: "SET_ORDER"; payload: { orderBy?: string; desc: boolean } }
  | { type: "SET_FILTERS"; payload: { fieldname: string; value: ColumnFilterValue } }
  | { type: "SET_SEARCH"; payload: string }
  | { type: "RESET_FILTERS" };

const reducer = (state: ListFilters, action: Action): ListFilters => {
  switch (action.type) {
    case "SET_PAGE":
      return { ...state, page: action.payload };
    case "NEXT_PAGE":
      return { ...state, page: state.page + 1 };
    case "PREV_PAGE":
      return { ...state, page: Math.max(1, state.page - 1) };
    case "SET_ORDER":
      return { ...state, orderBy: action.payload.orderBy, desc: action.payload.desc };
    case "SET_FILTERS": {
      const { fieldname, value } = action.payload;
      const otherFilters = state.filters?.filter((f) => f.fieldname !== fieldname) ?? [];
      const filterValues = value.values as MenuOptionFilterValue[];
      const newValues = filterValues.map((x) => (isLookup(x) ? x.id : x));
      const isEmpty = newValues.length === 0 && !value.operator;
      const updatedFilters = isEmpty
        ? otherFilters
        : [...otherFilters, { fieldname, operator: value.operator, values: newValues }];
      return { ...state, filters: updatedFilters };
    }
    case "SET_SEARCH":
      return { ...state, q: action.payload, page: 1 };
    case "RESET_FILTERS":
      return { ...state, filters: [] };
    default:
      return state;
  }
};

const useListFilters = (options?: Partial<UseListFiltersOptions>): UseListFiltersReturn => {
  const [searchParams, setSearchParams] = useSearchParams();
  const prefix = options?.prefix ?? "";

  const keys = {
    page: `${prefix}_page`,
    orderBy: `${prefix}_order_by`,
    order: `${prefix}_order`,
    filters: `${prefix}_filters`,
  };

  const initialFilters = [...(options?.filters ?? []), ...JSON.parse(searchParams.get(keys.filters) ?? "[]")];

  const initialState: ListFilters = {
    page: Number(searchParams.get(keys.page) ?? 1),
    orderBy: searchParams.get(keys.orderBy) ?? undefined,
    desc: searchParams.get(keys.order) === "desc",
    filters: initialFilters,
    limit: options?.limit ?? 50,
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  // Sync to URL when state changes
  useEffect(() => {
    const nextParams = new URLSearchParams(searchParams.toString());

    nextParams.set(keys.page, String(state.page));

    state.orderBy ? nextParams.set(keys.orderBy, state.orderBy) : nextParams.delete(keys.orderBy);

    nextParams.set(keys.order, state.desc ? "desc" : "asc");

    const currentFilterStr = searchParams.get(keys.filters);
    const nextFilterStr = state.filters?.length ? JSON.stringify(state.filters) : null;

    if (nextFilterStr && nextFilterStr !== currentFilterStr) {
      nextParams.set(keys.filters, nextFilterStr);
    } else if (!nextFilterStr && currentFilterStr) {
      nextParams.delete(keys.filters);
    }

    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [state]);

  // Sync from URL when search params change (e.g. back/forward button)
  useEffect(() => {
    const page = Number(searchParams.get(keys.page) ?? "1");
    const orderBy = searchParams.get(keys.orderBy) ?? undefined;
    const desc = searchParams.get(keys.order) === "desc";

    if (page !== state.page) dispatch({ type: "SET_PAGE", payload: page > 0 ? page : 1 });
    if (orderBy !== state.orderBy || desc !== state.desc) {
      dispatch({ type: "SET_ORDER", payload: { orderBy, desc } });
    }
    // If no filters are present in the URL, reset filters
    if (!searchParams.has(keys.filters)) {
      // dispatch({ type: "RESET_FILTERS" });
      console.log();
    }
  }, [searchParams]);

  return {
    state,
    nextPage: () => dispatch({ type: "NEXT_PAGE" }),
    prevPage: () => dispatch({ type: "PREV_PAGE" }),
    setSearchTerm: (term) => dispatch({ type: "SET_SEARCH", payload: term }),
    setOrder: (orderBy, desc) => dispatch({ type: "SET_ORDER", payload: { orderBy, desc } }),
    setFilters: (fieldname, value) => dispatch({ type: "SET_FILTERS", payload: { fieldname, value } }),
    getFilters: (fieldname) => {
      const filter = state.filters?.find((f) => f.fieldname === fieldname);
      return { operator: filter?.operator, values: filter?.values ?? [] } as StoredColumnFilterValue;
    },
    isSearchable: options?.isSearchable ?? false,
  };
};

export default useListFilters;
